import { useEffect, useRef } from 'react';
import { loadLeaflet } from '../../utils/geo';
import { addBaseLayer } from '../../utils/offline/baseLayer';

/*
 * The Leaflet map behind the national map page, and nothing else: tiles,
 * clustered pins, the dropped pin, and the two gestures that drop one.
 *
 *   * a click while "drop a pin" is on;
 *   * a long press anywhere (Leaflet reports it as contextmenu on touch screens,
 *     and a right click with a mouse).
 *
 * A plain click with drop mode off only pans and opens pins, so moving the map
 * on a phone never starts an observation by accident.
 *
 * leaflet.markercluster is a plain Leaflet plugin: it extends the global L,
 * which loadLeaflet() sets, so it is imported after it.
 */
export async function loadClusteredLeaflet() {
  await loadLeaflet();
  await Promise.all([
    import('leaflet.markercluster'),
    import('leaflet.markercluster/dist/MarkerCluster.css'),
    import('leaflet.markercluster/dist/MarkerCluster.Default.css'),
  ]);
  return window.L;
}

//: Israel, whole: the national map's first view when no pin says otherwise.
export const NATIONAL_VIEW = { center: [31.4, 35.0], zoom: 7 };

const COLORS = {
  observation: '#1e88e5',
  collection: '#2e7d32',
  public: '#ef6c00',
  local: '#757575',
};

export function pinStyle(point) {
  if (point.local) return 'local';
  if (point.source === 'public') return 'public';
  return point.kind === 'collection' ? 'collection' : 'observation';
}

function iconFor(L, point) {
  const style = pinStyle(point);
  const shape = style === 'collection' ? 'border-radius:4px;transform:rotate(45deg);' : 'border-radius:50%;';
  const border = style === 'local' ? 'border:2px dashed #fff;' : 'border:2px solid #fff;';
  const check = point.verification === 'verified'
    ? '<span style="position:absolute;top:-6px;right:-6px;background:#fff;color:#2e7d32;border-radius:50%;'
      + 'width:14px;height:14px;font-size:11px;line-height:14px;text-align:center;font-weight:700;">✓</span>' : '';
  return L.divIcon({
    className: `pitriot-pin pitriot-pin-${style}`,
    html: `<span style="position:relative;display:block;width:22px;height:22px;">`
      + `<span style="display:block;width:18px;height:18px;margin:2px;background:${COLORS[style]};${shape}${border}`
      + `box-shadow:0 1px 4px rgba(0,0,0,.45);"></span>${check}</span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

//: How close "Show on map" (a `focus`) opens: a few streets across.
export const FOCUS_ZOOM = 15;

/** The phone's own position: a blue dot (its accuracy is a circle beside it). */
export function youIcon(L) {
  return L.divIcon({
    className: 'pitriot-you',
    html: '<span style="display:block;width:16px;height:16px;border-radius:50%;background:#1565c0;'
      + 'border:3px solid #fff;box-shadow:0 0 0 2px rgba(21,101,192,.35),0 1px 4px rgba(0,0,0,.5);"></span>',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

/** The target: a ring with a dot, unlike any pin, so it reads as "go here". */
export function targetIcon(L) {
  return L.divIcon({
    className: 'pitriot-target',
    html: '<span style="display:block;width:26px;height:26px;border-radius:50%;border:3px solid #d81b60;'
      + 'background:rgba(255,255,255,.85);box-shadow:0 1px 6px rgba(0,0,0,.5);position:relative;">'
      + '<span style="position:absolute;inset:7px;border-radius:50%;background:#d81b60;"></span></span>',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

/** Where the car is left: a "P" in a square. */
export function parkIcon(L) {
  return L.divIcon({
    className: 'pitriot-park',
    html: '<span style="display:block;width:22px;height:22px;border-radius:5px;background:#1565c0;color:#fff;'
      + 'font:700 14px/22px sans-serif;text-align:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5);">P</span>',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

//: How each leg of a route is drawn (utils/routing/astar.js).
export const ROUTE_STYLE = {
  drive: { color: '#1565c0', weight: 5, opacity: 0.85 },
  walk: { color: '#2e7d32', weight: 4, dashArray: '8 6' },
  offTrail: { color: '#d81b60', weight: 3, dashArray: '2 6' },
  approach: { color: '#616161', weight: 3, dashArray: '2 6' },
};

/**
 * The position, its accuracy, the target and the way between them, on a map:
 * removes what `layers` held and draws them again. Shared by the national map
 * and the camera's mini-map (components/field/FieldTargetMap.js). Returns the
 * new layers.
 *
 * With a `route` (utils/routing/useRoute), the way is its legs: the drive
 * solid, the walk dashed, the last steps off any path dotted, and a P where
 * the car is left. Without one -- no data, no route found, or still being
 * found -- it is the straight line it always was.
 */
export function drawNavigation(L, map, layers, { you = null, target = null, route = null } = {}) {
  for (const layer of layers || []) layer.remove();
  const next = [];
  const here = you && Number.isFinite(Number(you.lat)) && Number.isFinite(Number(you.lng))
    ? [Number(you.lat), Number(you.lng)] : null;
  const there = target && Number.isFinite(Number(target.lat)) && Number.isFinite(Number(target.lng))
    ? [Number(target.lat), Number(target.lng)] : null;
  if (route && there) {
    for (const name of ['approach', 'drive', 'walk', 'offTrail']) {
      const points = route[name]?.points;
      if (points && points.length > 1) {
        next.push(L.polyline(points, { ...ROUTE_STYLE[name], interactive: false }).addTo(map));
      }
    }
    if (route.dropOff) {
      next.push(L.marker([route.dropOff.lat, route.dropOff.lng], { icon: parkIcon(L), interactive: false,
        keyboard: false }).addTo(map));
    }
  } else if (here && there) {
    next.push(L.polyline([here, there], { color: '#d81b60', weight: 3, dashArray: '6 6', interactive: false })
      .addTo(map));
  }
  if (here) {
    if (Number.isFinite(Number(you.accuracy_m)) && Number(you.accuracy_m) > 0) {
      next.push(L.circle(here, { radius: Number(you.accuracy_m), color: '#1565c0', weight: 1, fillOpacity: 0.12,
        interactive: false }).addTo(map));
    }
    next.push(L.marker(here, { icon: youIcon(L), interactive: false, keyboard: false }).addTo(map));
  }
  if (there) next.push(L.marker(there, { icon: targetIcon(L), interactive: false, keyboard: false }).addTo(map));
  return next;
}

/** Where the map first looks: the focused record, else all of Israel. */
export function initialView(focus) {
  const ok = focus && Number.isFinite(focus.lat) && Number.isFinite(focus.lng);
  return ok ? { center: [focus.lat, focus.lng], zoom: FOCUS_ZOOM } : NATIONAL_VIEW;
}

/*
 * `focus` {lat, lng} (Home's "Show on map") is read once, when the map is
 * made: it opens there, and the first fit to the pins is skipped so it stays
 * there. A later filter change fits the pins as usual.
 *
 * `target` {lat, lng} is the observation being walked to and `you` {lat, lng,
 * accuracy_m} the phone's live fix (only while the page follows it): drawn
 * outside the cluster, with the line between them, redrawn as either moves.
 * A new target is panned to once; a moving fix never pulls the map about.
 */
export default function MapCanvas({ points = [], localPoints = [], dropMode = false, dropped = null,
  onSelect, onPlace, fitKey = '', focus = null, label, you = null, target = null, route = null }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const droppedRef = useRef(null);
  const navRef = useRef([]);
  const shownTarget = useRef('');
  const handlers = useRef({});
  handlers.current = { onSelect, onPlace, dropMode };
  const view = useRef(initialView(focus));
  // A focused map counts its first fit as done, so the pins do not pull it away.
  const fitted = useRef(view.current === NATIONAL_VIEW ? '' : null);

  useEffect(() => {
    let live = true;
    loadClusteredLeaflet().then((L) => {
      if (!live || !containerRef.current || mapRef.current) return;
      const map = L.map(containerRef.current, { zoomControl: true })
        .setView(view.current.center, view.current.zoom);
      addBaseLayer(L, map, { attribution: '© OpenStreetMap contributors', maxZoom: 19 });
      const cluster = L.markerClusterGroup({ showCoverageOnHover: false, maxClusterRadius: 50, chunkedLoading: true });
      map.addLayer(cluster);
      const place = (e) => handlers.current.onPlace?.({ lat: e.latlng.lat, lng: e.latlng.lng });
      map.on('click', (e) => { if (handlers.current.dropMode) place(e); });
      map.on('contextmenu', place);
      mapRef.current = map;
      clusterRef.current = cluster;
      map.fire('pitriot:ready');
      setTimeout(() => map.invalidateSize(), 0);
    }).catch(() => {});
    return () => {
      live = false;
      mapRef.current?.remove();
      mapRef.current = null;
      clusterRef.current = null;
      droppedRef.current = null;
      navRef.current = [];
    };
  }, []);

  // The way to the target: drawn once the map exists, again on every move.
  const youLat = you?.lat;
  const youLng = you?.lng;
  const youAccuracy = you?.accuracy_m;
  const targetLat = target?.lat;
  const targetLng = target?.lng;
  useEffect(() => {
    let live = true;
    const draw = () => {
      const L = window.L;
      const map = mapRef.current;
      if (!live || !L || !map) return false;
      navRef.current = drawNavigation(L, map, navRef.current, {
        you: youLat != null ? { lat: youLat, lng: youLng, accuracy_m: youAccuracy } : null,
        target: targetLat != null ? { lat: targetLat, lng: targetLng } : null,
        route,
      });
      const key = targetLat != null ? `${targetLat},${targetLng}` : '';
      if (key && key !== shownTarget.current) map.panTo([Number(targetLat), Number(targetLng)]);
      shownTarget.current = key;
      return true;
    };
    if (!draw()) {
      const timer = setInterval(() => { if (draw()) clearInterval(timer); }, 100);
      return () => { live = false; clearInterval(timer); };
    }
    return () => { live = false; };
  }, [youLat, youLng, youAccuracy, targetLat, targetLng, route]);

  // The pins, redrawn when the filtered set changes.
  useEffect(() => {
    let live = true;
    const draw = () => {
      const L = window.L;
      const map = mapRef.current;
      const cluster = clusterRef.current;
      if (!live || !L || !map || !cluster) return false;
      cluster.clearLayers();
      const all = [...points, ...localPoints];
      cluster.addLayers(all.map((p) => {
        const marker = L.marker([p.lat, p.lng], { icon: iconFor(L, p), keyboard: true,
          title: p.taxon_name || p.code || '' });
        marker.on('click', () => handlers.current.onSelect?.(p));
        return marker;
      }));
      // Fit once per filter, not on every refresh of the same set; a focused
      // map takes its first filter as already fitted.
      if (fitted.current === null) fitted.current = fitKey;
      if (all.length && fitted.current !== fitKey) {
        fitted.current = fitKey;
        map.fitBounds(L.latLngBounds(all.map(p => [p.lat, p.lng])), { padding: [30, 30], maxZoom: 13 });
      }
      return true;
    };
    if (!draw()) {
      const timer = setInterval(() => { if (draw()) clearInterval(timer); }, 100);
      return () => { live = false; clearInterval(timer); };
    }
    return () => { live = false; };
  }, [points, localPoints, fitKey]);

  // The pin being added.
  useEffect(() => {
    const L = window.L;
    const map = mapRef.current;
    if (!L || !map) return;
    if (droppedRef.current) { droppedRef.current.remove(); droppedRef.current = null; }
    if (dropped) {
      // Drawn, not L.Icon.Default: Leaflet finds its default marker image by
      // a CSS path a bundled build does not keep, and showed the word "Marker".
      droppedRef.current = L.marker([dropped.lat, dropped.lng], { icon: L.divIcon({
        className: 'pitriot-pin-dropped',
        html: '<span style="display:block;width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);'
          + 'background:#d81b60;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.5);"></span>',
        iconSize: [26, 26],
        iconAnchor: [13, 30],
      }) }).addTo(map);
      map.panTo([dropped.lat, dropped.lng]);
    }
  }, [dropped]);

  return (
    <div ref={containerRef} role="application" aria-label={label} data-testid="map-canvas"
      style={{ position: 'absolute', inset: 0, cursor: dropMode ? 'crosshair' : undefined }} />
  );
}
